import css from "./guarantee.module.scss";
import { Layout } from "../../../componentsV1/layout";
import Logo from "../../../assets/v1/logo.png";

export const Guarantee = () => {
  const workflows = [
    {
      title: "Register Your Product",
      description: ['Register your purchased Identifyn® reagent through your online account.']
    },
    {
      title: "Download the Methods",
      description: ['Download and review the associated Identifyn® methods, protocols, and SOP documentation.']
    },
    {
      title: "Follow the Recommended Workflow",
      description: ['Perform the experiment using the recommended methodology and imaging guidelines.']
    },
    {
      title: "Contact Scientific Support",
      description: ['If performance concerns arise, contact Identifyn® before abandoning the experiment.', 'Our team will review the experimental design, controls, imaging data, and protocol details to identify potential issues and recommend corrective actions.']
    },
    {
      title: "Resolution",
      description: ['If performance concerns remain after scientific review and troubleshooting, replacement product may be provided.']
    }
  ]

  return (
    <Layout>
      <div className={css.heroContainer}>
        <div className={css.heroContent}>
          <img src={Logo} alt="logo" className={css.heroLogo} />
          <div className={css.heroText}>
            <h1>The Identifyn® Performance Guarantee</h1>
            <div className={css.line} />
          </div>
        </div>
      </div>
      <div className={css.container}>
        {/* first section */}
        <div className={css.card}>
          <div className={css.row}>
            <div className={`${css.column} ${css.flex}`}>
              <h5 className={css.specialText}>Why We Can Make This Guarantee</h5>
              <h2 className={css.heading}>Patent-Pending Stepdown™ Methodology</h2>
              <p>Stepdown™ is a resolution-aware validation framework designed to challenge reagent performance across the complete imaging workflow.</p>
              <p>Rather than validating performance at a single optical scale, Stepdown™ evaluates performance from conventional widefield microscopy through confocal, Airyscan, structured illumination microscopy, and single-molecule imaging approaching 20 nm resolution.</p>
              <p>This process identifies failures conventional validation approaches often miss.</p>
              <p>A reagent that survives Stepdown™ has been challenged at every scale.</p>
            </div>
            <div className={`${css.column} ${css.flex}`} style={{ gap: '12px', justifyContent: 'center' }}>
              <div className={css.pillCard}>
                <p>Resolution-dependent specificity loss</p>
              </div>
              <div className={css.pillCard}>
                <p>Localization artifacts</p>
              </div>
              <div className={css.pillCard}>
                <p>Signal degradation</p>
              </div>
              <div className={css.pillCard}>
                <p>Conjugation-related performance changes</p>
              </div>
              <div className={css.pillCard}>
                <p>Optical-scale dependent biological misinterpretation</p>
              </div>
              <div className={css.pillCard}>
                <p>PStructural inaccuracies hidden at lower resolution</p>
              </div>
            </div>
          </div>
        </div>
        {/* second section */}
        <div className={css.column}>
          <h5 className={css.specialText}>Why We Can Make This Guarantee</h5>
          <div className={css.row}>
            <h2 className={`${css.heading} ${css.flex}`}>Patent-Pending Conjugation Chemistry</h2>
            <div className={`${css.column} ${css.flex}`}>
              <p>Identifyn® SRM Alexa Fluor™ Direct Conjugates and Identifyn® SRM Alexa Fluor™ Secondary Antibodies are produced using proprietary conjugation methodologies designed to maximize fluorophore performance while preserving target recognition and biological relevance.</p>
              <p>Our patent-pending conjugation chemistry was developed to improve brightness, preserve specificity, and maintain localization accuracy under demanding imaging conditions.</p>
              <p>The result is robust fluorescence performance across applications ranging from conventional microscopy to super-resolution imaging.</p>
            </div>
          </div>
        </div>
        {/* third section */}
        <div className={css.card}>
          <div className={css.row}>
            <div className={`${css.column} ${css.flex}`}>
              <h2 className={css.heading}>What is Covered?</h2>
              <p>Qualifying Identifyn® products are supported by:</p>
              <p>Our goal is not simply ot replace a reagent. Our goal is to help ensure a successful experiment.</p>
            </div>
            <div className={`${css.column} ${css.flex}`} style={{ gap: '12px', justifyContent: 'center' }}>
              <div className={css.pillCard}>
                <p>Scientific troubleshooting assistance</p>
              </div>
              <div className={css.pillCard}>
                <p>Experimental review by the Identifyn team</p>
              </div>
              <div className={css.pillCard}>
                <p>Protocol optimization recommendations</p>
              </div>
              <div className={css.pillCard}>
                <p>Product replacement when appropriate</p>
              </div>
              <div className={css.pillCard}>
                <p>Direct access to imaging and assay development expertise</p>
              </div>
            </div>
          </div>
        </div>
        {/* fourth section */}
        <div className={css.column}>
          <div className={`${css.column} ${css.centerText}`}>
            <h2 className={css.heading}>How the Guarantee Works</h2>
          </div>
          <div className={css.twoColumn}>
            {
              workflows.map((workflow, index) => (
                <div className={`${css.row} ${css.card}`} key={index} style={{ height: 'auto' }}>
                  <div className={css.numberLabel}>
                    {(index + 1).toString().padStart(2, '0')}
                  </div>
                  <div className={css.column}>
                    <p>{workflow.title}</p>
                    <div className={css.divider} />
                    {workflow.description.map((item, index) => (
                      <p key={index}>{item}</p>
                    ))}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
        {/* fifth section */}
        <div className={css.card}>
          <div className={css.singleColumn}>
            <div className={css.row}>
              <h2 className={`${css.heading} ${css.flex}`}>What Makes This Different?</h2>
              <div className={`${css.column} ${css.flex}`}>
                <p>Most guarantees focus on replacing a product.</p>
                <p>The Identifyn® Performance Guarantee focuses on ensuring experimental success.</p>
                <p>We believe scientific expertise, imaging experience, and rigorous validation provide greater value than simply issuing a replacement.</p>
                <p>Every qualifying reagent is supported by both technology and scientific guidance.</p>
              </div>
            </div>
            <div className={css.divider} />
            <div className={css.row}>
              <h2 className={`${css.heading} ${css.flex}`}>Built on Validation. Not Assumption.</h2>
              <div className={`${css.column} ${css.flex}`}>
                <p>Identifyn® reagents are evaluated across a growing body of multimodal imaging datasets generated using the Stepdown™ framework.</p>
                <p>By examining performance across optical scales, we evaluate not only whether a reagent produces signal, but whether that signal remains biologically meaningful as resolution increases.</p>
                <p>This approach provides a level of validation that conventional single-resolution testing cannot achieve.</p>
              </div>
            </div>
            <div className={css.divider} />
            <div className={css.row}>
              <h2 className={`${css.heading} ${css.flex}`}>Performance. Proven. Guaranteed.</h2>
              <div className={`${css.column} ${css.flex}`}>
                <p>Most companies sell reagents.</p>
                <p>Identifyn® validates them.</p>
                <p>Every qualifying Identifyn® reagent is challenged through our patent-pending Stepdown™ Methodology and supported by our patent-pending conjugation chemistry before it reaches your laboratory.</p>
              </div>
            </div>
          </div>
        </div>
        {/* sixth section */}
        <div className={`${css.column} ${css.centerText} ${css.smallContainer} ${css.card}`}>
          <h5 className={css.specialText}>Proven from Widefield to Single Molecule. <br /> Guaranteed Everywhere in Between.</h5>
          <h2 className={css.heading}>Antibodies You Can Trust.<br />
            Data You Can Defend. <br />
            Performance Guaranteed.</h2>
        </div>
      </div>
    </Layout>
  );
}